<template>
  <div class="misc-wrapper">
    <b-link class="brand-logo">
      <habit-logo />
      <h2 class="brand-text text-primary ml-1">
        Habit
      </h2>
    </b-link>

    <!-- Loading -->
    <loading
      v-if="isLoading || isSubmitting"
      :active="true" 
      :is-full-page="true"
      :color="colors.primary"
    />

    <div v-if="!isLoading" class="misc-inner p-2 p-sm-3 mt-5">
      <div class="w-100 text-center">
        <h2 v-if="qrExpired" class="text-danger">
          {{ $t('qr_not_valid_check_new_code') }}
        </h2>

        <h2 v-else-if="surveyNotFound" class="text-danger">
          {{ $t('survey_not_found_check_url') }}
        </h2>
      
        <div v-else>
          <!-- <h5 v-if="!isSubmitted" class="mb-2">
            <u>{{ $t('label.Location') }}</u>: <b>{{ survey.location }}</b>
          </h5> -->

          <h5 v-if="!isSubmitted" class="mb-2 mb-sm-5 d-flex justify-content-center flex-column">
            <u style="text-decoration: none;" v-if="survey.physical_location" class="mb-2"><b>{{ survey.physical_location }}</b></u>
            <u style="text-decoration: none;">{{ $t('label.Location') }}: <b>{{ survey.location }}</b></u>
          </h5>

          <h2 class="mb-2">
            {{ isSubmitted ? $t('thank_you_completing_survey') : survey.name }}
          </h2>

          <!-- Survey -->
          <div v-if="!isSubmitted">
            <!-- Questions -->
            <b-card v-for="(item, index) in survey.questions" :key="index" class="mb-3 text-left">
              <h5 class="mb-2">{{ index + 1 }}. {{ item.text }}</h5>
              <b-form-rating
                v-model="item.answer"
                stars="5"
                variant="warning"
                show-value
              />
            </b-card>

            <!-- Comment -->
            <b-card class="mb-3 text-left">
              <h5 class="mb-2">{{ survey.comment }}</h5>
              <b-form-textarea
                v-model="commentAnswer"
                rows="3"
                max-rows="6"
                :placeholder="$t('write_your_answer')"
              />
            </b-card>
          </div>

          <b-button
            v-if="!isSubmitted"
            variant="primary"
            class="btn-lg"
            :disabled="isSubmitting"
            @click="submit"
          >
            {{ $t('message.submit') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BLink, BButton, BFormTextarea, BFormGroup, BFormRating } from 'bootstrap-vue'
import HabitLogo from '@core/layouts/components/Logo.vue'
import { ref, onMounted, computed } from "@vue/composition-api"
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import useNotifications from "@/composables/useNotifications"
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import useJwt from '@/auth/jwt/useJwt'
import { colors } from '@/constants'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { qrBaseURL } from '@/views/organization/qr-generator/qrCodeImage'

export default {
  components: {
    HabitLogo,
    BLink,
    BButton,
    Loading,
    BFormTextarea,
    BFormGroup,
    BFormRating,
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { route } = useRouter()
    const { getItemsAggregate, getItems, createItem } = useLambda()
    const survey = ref({})
    const surveyNotFound = ref(false)
    const qrExpired = ref(false)
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const isSubmitted = ref(false)
    const commentAnswer = ref('')
    const query = { _id: oid(route.value.params.surveyId) }

    // Validates that the QR code used to reach this page is still active.
    // QRs created before this feature have no token and keep working.
    const validateQRCode = async () => {
      try {
        const token = route.value.query.t
        const urlSuffix = `/barometer-survey/${route.value.params.surveyId}`

        const items = await getItems({ collection: 'qr-code', query: { destination: 'barometer-survey', deleted: { $ne: true } } })
        const matching = items.filter(item => qrBaseURL(item.url).endsWith(urlSuffix))
        // No registry for this survey => legacy QR, still valid.
        // If a registry exists, it must not be revoked and its token (if any) must match.
        qrExpired.value = matching.length > 0 && !matching.some(item => !item.revoked && (item.token ? item.token === token : true))
      } catch (error) {
        console.log(error)
      }
    }

    const isSurveyComplete = computed(() => {
      return survey.value.questions?.every(item => Boolean(item.answer))
    })

    const submit = async () => {
      if (!isSurveyComplete.value) {
        showErrorMessage(i18n.t('must_answer_all_questions'))
        return
      }

      isSubmitting.value = true

      const payload = {
        surveyCreated: oid(route.value.params.surveyId),
        createdAt: date(new Date()),
        answers: survey.value.questions.map(item => ({
          category: item.category,
          question: item.text,
          answer: item.answer,
        })),
        comment: {
          question: survey.value.comment,
          answer: commentAnswer.value,
        },
        qr_token: route.value.query.t || null,
      }

      try {
        await createItem({ collection: 'barometer_answer', payload })
        showSuccessMessage(i18n.t('message.survey_success'))
        isSubmitted.value = true
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.survey_submit_error'))
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(async () => {
      // Define locale based on the language of the browser UI
      if (window.navigator.language?.startsWith('es')) i18n.locale = 'es'

      try {
        // If user is not logged in (either as a real or anonymous user), then log in as an anonymous user
        // if (!useJwt.realm.currentUser) await useJwt.loginAsAnonymousUser()

        // Get survey data based on survey id from URL param
        const pipeline = [
          { $match: query },
          {
            $lookup: {
              from: 'location',
              localField: 'location',
              foreignField: '_id',
              as: 'locationData'
            }
          },
          {
            $lookup: {
              from: 'physical_location',
              localField: 'physical_location',
              foreignField: '_id',
              as: 'physicalLocationData'
            }
          },
          {
            $addFields: {
              location: { $arrayElemAt: ['$locationData.location', 0] },
              physical_location: { $arrayElemAt: ['$physicalLocationData.name', 0] }
            }
          },
          {
            $project: {
              locationData: 0,
              physicalLocationData: 0
            }
          }
        ]
        const [items] = await Promise.all([
          getItemsAggregate({ collection: 'barometer_survey_created', pipeline }),
          validateQRCode(),
        ])
        if (!items?.[0]) throw new Error('Item not found')

        survey.value = items[0]
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.survey_fetch_error'))
        surveyNotFound.value = true
      } finally {
        isLoading.value = false
      }
    })

    return {
      surveyNotFound,
      qrExpired,
      submit,
      isLoading,
      isSubmitting,
      isSubmitted,
      colors,
      survey,
      isSurveyComplete,
      commentAnswer,
    };
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-misc.scss';
</style>